import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {CallbackPayload} from '@shared-library';
import {MessageBusEventPayload} from '@shared-library';
import {EventTypes} from '@shared-library';
import {ConfigService} from '@shared-library';
import {ConnectPayload} from '@shared-library';

@Injectable({
  providedIn: 'root'
})
export class MessageBusService {

  //this subject is populated when in popup mode, and a Portal/Endpoint has been selected.
  // A separate window will be opened, and credentials & metadata from that connection will be passed to this callback fn.
  // SEE: https://docs.connect.fastenhealth.com/stitch/webcomponent for example data structure.
  // {
  //   "org_connection_id": "1234-1234-1234-1234-1234",
  //   "endpoint_id": "1234-1234-1234-1234-1234"
  // }
  public messageBusSubject = new BehaviorSubject<MessageBusEventPayload | null>(null)

  constructor(private configService: ConfigService) { }

  //notify the parent element that we could not register Fasten Connect installation: missing or invalid id
  public publishWidgetConfigError(): void {
    let eventPayload = new MessageBusEventPayload()
    eventPayload.api_mode = this.configService.systemConfig$.apiMode
    eventPayload.event_type = EventTypes.EventTypeWidgetConfigError
    this.messageBusSubject.next(eventPayload)
  }


  //this event is published when the popup window is opened.
  public publishOrgConnectionPending(pendingConnectData: ConnectPayload): void {
    let eventPayload = new MessageBusEventPayload()
    eventPayload.api_mode = this.configService.systemConfig$.apiMode
    eventPayload.data = pendingConnectData
    eventPayload.event_type = EventTypes.EventTypeConnectionPending
    this.messageBusSubject.next(eventPayload)
  }

  //this event is published when the popup window is closed, and the connection is successful or failed.
  public publishOrgConnectionComplete(orgConnectionCallbackData: CallbackPayload): void {
    const {linked_connections: linkedConnections = [], ...primaryConnection} = orgConnectionCallbackData

    this.publishSingleOrgConnectionComplete(primaryConnection)
    linkedConnections.forEach((linkedConnection) => {
      const {linked_connections: _nestedLinkedConnections, ...linkedConnectionData} = linkedConnection
      this.publishSingleOrgConnectionComplete(linkedConnectionData as CallbackPayload)
    })
  }

  private publishSingleOrgConnectionComplete(orgConnectionCallbackData: CallbackPayload): void {
    const eventPayload = new MessageBusEventPayload()
    eventPayload.api_mode = this.configService.systemConfig$.apiMode
    eventPayload.data = orgConnectionCallbackData
    if (orgConnectionCallbackData.error) {
      eventPayload.event_type = EventTypes.EventTypeConnectionFailed
    } else {
      eventPayload.event_type = EventTypes.EventTypeConnectionSuccess
    }
    this.messageBusSubject.next(eventPayload)
  }


  //this event is published when the widget is closed. It will publish a list of all
  public publishComplete(): void {
    let eventPayload = new MessageBusEventPayload()
    eventPayload.api_mode = this.configService.systemConfig$.apiMode
    eventPayload.data = this.configService.vaultProfileConfig$.connectedPatientAccounts?.map((account) => {
      return {
        connection_status: account.connection_status,
        org_connection_id: account.org_connection_id,
        platform_type: account.platform_type,
        brand_id: account.brand?.id,
        portal_id: account.portal?.id,
        endpoint_id: account.endpoint?.id,
        scope: account.scope,
        consent_expires_at: account.consent_expires_at,
        tefca_directory_id: account.tefca_directory_id,
      }
    }) || []
    eventPayload.event_type = EventTypes.EventTypeWidgetComplete
    this.messageBusSubject.next(eventPayload)
  }

  //this event is published when the widget requests to close the modal.
  public publishRequestClose(): void {
    let eventPayload = new MessageBusEventPayload()
    eventPayload.api_mode = this.configService.systemConfig$.apiMode
    eventPayload.event_type = EventTypes.EventTypeWidgetClose
    this.messageBusSubject.next(eventPayload)
  }

  //this event is published when a widget search is performed
  public publishSearchQuery(query: string, locations?: string[], external_id?: string, total_results?: number): void {
    if(!this.configService.systemConfig$.eventTypes?.includes(EventTypes.EventTypeSearchQuery)){
      return
    }
    if(!query?.trim()){
      return;
    }
    let eventPayload = new MessageBusEventPayload();
    eventPayload.api_mode = this.configService.systemConfig$.apiMode;
    eventPayload.event_type = EventTypes.EventTypeSearchQuery;
    eventPayload.data = {
      query: query,
      timestamp: Date.now(),
      filter: {
        locations: locations || []
      },
      results: {
        total: total_results
      },
      external_id
    };
    this.messageBusSubject.next(eventPayload);
  }
}
